<template>
	<AppBottomSheet :active="active" :force-width="bottomSheetWidth" @toggling-popup="open => $emit('toggling-popup', open)">
		<div v-if="active">
			<ModalHeader
				:title="getString('manual_invoice_title')"
				:button-text="getString('manual_invoice_emit')"
				:button-disabled="!isFormValid"
				:button-loading="emitting"
				@click-button="emitInvoice" />
			<AppList :items="listItems">
				<template #date="{item}">
					<div class="manual-row">
						<div class="manual-row-texts">
							<p class="manual-row-title">{{ item.title }}</p>
							<p v-if="item.hint" class="manual-row-hint">{{ item.hint }}</p>
						</div>
						<AppTextInput class="manual-row-input" type="date" :value="$data[item.field]" @changing-text="v => setField(item.field, v)" />
					</div>
				</template>
				<template #text="{item}">
					<div class="manual-row">
						<p class="manual-row-title">{{ item.title }}</p>
						<AppTextInput class="manual-row-input" :value="$data[item.field]" :placeholder="item.placeholder" @changing-text="v => setField(item.field, v)" />
					</div>
				</template>
				<template #lines>
					<div class="manual-lines-row">
						<p class="manual-row-title">{{ getString('manual_invoice_lines') }}</p>
						<ManualInvoiceLines v-model="lines" :vat-rates="vatRates" :price-label="getString('manual_invoice_unit_price_vat')" with-nature />
					</div>
				</template>
			</AppList>
		</div>
	</AppBottomSheet>

	<!-- Rendered after the sheet so it stacks over it -->
	<CustomersPicker companies-only :active="pickerOpen" @toggling-popup="closePicker" @picked-customer="pickedCustomer" />
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import CustomersPicker from "@/console-new/components/ops/CustomersPicker.vue";
import ManualInvoiceLines, {emptyLine, isoDate, isValidQuantity, INVALID_DOCUMENT_ERRORS} from "@/console-new/components/config/administration/ManualInvoiceLines.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import API from "@/shared/helpers/API";
import {useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";

const VAT_RATES = [22, 10, 5, 4, 0];
const PAYMENT_TYPES = ["cash", "stripe", "wire", "check"];
// SDI accepts an invoice dated up to 12 days back
const MAX_DAYS_BACK = 12;

/**
 * An invoice (TD01) to a company customer, typed in Fiscalità with no orders behind it
 */
export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {userStore, currencyStore, getString: languageStore.getString, showNotification: notificationStore.showNotification};
	},
	name: "ManualInvoiceBottomSheet",
	components: {AppBottomSheet, AppList, AppTextInput, ModalHeader, CustomersPicker, ManualInvoiceLines},
	mixins: [WindowWidth],
	emits: ["toggling-popup", "emitted"],
	props: {
		active: {type: Boolean, default: false},
	},
	data() {
		return {
			customer: null,
			date: isoDate(new Date()),
			paymentType: PAYMENT_TYPES[0],
			paid: true,
			dueDate: "",
			purchaseOrder: "",
			causale: "",
			lines: [emptyLine(VAT_RATES[0])],
			vatRates: VAT_RATES,
			pickerOpen: false,
			emitting: false,
		};
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "760px";
			}
		},
		minDate() {
			let date = new Date();
			date.setDate(date.getDate() - MAX_DAYS_BACK);
			return isoDate(date);
		},
		total() {
			return this.lines.reduce((total, line) => total + Math.round(line.price * 100) * line.quantity, 0);
		},
		isFormValid() {
			return !!this.customer
				&& !!this.date && this.date >= this.minDate && this.date <= isoDate(new Date())
				&& (this.paid || (!!this.dueDate && this.dueDate >= this.date))
				&& this.lines.length > 0
				&& this.lines.every(line => !!line.description.trim() && isValidQuantity(line.quantity) && line.price > 0 && (line.vat !== 0 || !!line.natura_iva));
		},
		listItems() {
			let items = [
				{
					uniqueId: "customer",
					title: this.customer ? this.customer.name : this.getString("customer"),
					explain: this.customer && this.customer.vat ? this.getString("vat") + " " + this.customer.vat : null,
					buttonTitle: this.getString(this.customer ? "manual_invoice_change_customer" : "manual_invoice_select_customer"),
					callbackButton: () => this.pickerOpen = true,
				},
				{
					uniqueId: "date",
					title: this.getString("manual_invoice_date"),
					hint: this.getString("manual_invoice_date_hint", [String(MAX_DAYS_BACK)]),
					customSlot: "date",
					field: "date",
				},
				{
					uniqueId: "payment",
					title: this.getString("payment"),
					selectValues: PAYMENT_TYPES.map(type => this.getString("payment_type_" + type)),
					selectedIndex: PAYMENT_TYPES.indexOf(this.paymentType),
					callbackSelect: index => this.paymentType = PAYMENT_TYPES[index],
				},
				{
					uniqueId: "paid",
					title: this.getString("manual_invoice_paid"),
					switch: true,
					active: this.paid,
					callback: value => this.paid = value,
				},
			];
			if(!this.paid) {
				items.push({uniqueId: "due-date", title: this.getString("manual_invoice_due_date"), customSlot: "date", field: "dueDate"});
			}
			items.push(
				{uniqueId: "purchase-order", title: this.getString("purchase_order"), customSlot: "text", field: "purchaseOrder", placeholder: this.getString("manual_invoice_optional")},
				{uniqueId: "causale", title: this.getString("manual_invoice_causale"), customSlot: "text", field: "causale", placeholder: this.userStore.currBusiness ? this.userStore.currBusiness.name : ""},
				{uniqueId: "lines", title: this.getString("manual_invoice_lines"), customSlot: "lines"},
				{uniqueId: "total", title: this.getString("total"), rightText: this.currencyStore.readableCurrencyValue(this.total), rightPrimaryText: true},
			);
			return items;
		},
	},
	methods: {
		reset() {
			Object.assign(this.$data, this.$options.data.call(this));
		},
		setField(field, value) {
			this[field] = value;
		},
		closePicker(isOpen) {
			if(!isOpen) {
				this.pickerOpen = false;
			}
		},
		pickedCustomer(customer) {
			if(!customer || !customer.customer_id) {
				this.showNotification("invoice-switch-customer-missing");
				return;
			}
			this.customer = customer;
			this.pickerOpen = false;
		},
		emitInvoice() {
			if(!this.isFormValid || this.emitting) {
				return;
			}
			this.emitting = true;
			API.init().emitManualInvoice(this.userStore.currBusinessId, {
				customer_id: this.customer.customer_id,
				date: this.date,
				payment_type: this.paymentType,
				invoice_paid: this.paid,
				due_date: this.paid ? "" : this.dueDate,
				purchase_order: this.purchaseOrder,
				causale: this.causale,
				items: this.lines.map(line => ({
					description: line.description,
					quantity: line.quantity,
					price: Math.round(line.price * 100),
					vat: line.vat,
					natura_iva: line.vat === 0 ? line.natura_iva : null,
				})),
			}, (response) => {
				this.emitting = false;
				if(response && response.result === "OK") {
					this.showNotification("generic-success");
					this.$emit("emitted");
					this.$emit("toggling-popup", false);
				} else if(response && response.details === "invoice_emission_failed") {
					// Saved as failed: it is retried from the table
					this.showNotification("fiscal-document-emission-failed", [String(response.invoice?.error || "-")]);
					this.$emit("emitted");
					this.$emit("toggling-popup", false);
				} else if(response && INVALID_DOCUMENT_ERRORS.includes(response.details)) {
					this.showNotification("fiscal-document-invalid", [this.getString("fiscal_document_error_" + response.details)]);
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
	},
	watch: {
		active(isActive) {
			if(isActive) {
				this.reset();
			} else {
				this.pickerOpen = false;
			}
		},
	},
}
</script>

<style scoped lang="scss">
.manual-row, .manual-lines-row {
	width: 100%;
	padding: 10px 0;
}

.manual-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 10px;

	.manual-row-input {
		width: 240px;
		max-width: 100%;
	}
}

.manual-lines-row {
	display: flex;
	flex-direction: column;
	gap: 12px;
}


.manual-row-hint {
	font-size: 0.85em;
	color: var(--color-txt-2);
}
</style>
